import { computed, inject, Injectable } from '@angular/core';
import {
    NonNullableFormBuilder,
    Validators,
    type FormControl,
    type FormGroup,
} from '@angular/forms';
import type { StartBuildingOwnershipTransferRequest } from '@quipex/shared/contracts';
import {
    emailValidator,
    toSignalForm,
    type FormValues,
    type ReactiveFormState,
} from '@quipex/shared/utils';
import { defer, EMPTY, of, type Observable } from 'rxjs';

export interface TransferBuildingOwnershipFormControls {
    recipientEmail: FormControl<string>;
    retainOriginalOwnerAccess: FormControl<boolean>;
}

export type TransferBuildingOwnershipForm =
    FormGroup<TransferBuildingOwnershipFormControls>;

type TransferBuildingOwnershipFormValues =
    FormValues<TransferBuildingOwnershipForm>;

@Injectable()
export class FormState implements ReactiveFormState<
    TransferBuildingOwnershipForm,
    StartBuildingOwnershipTransferRequest
> {
    //#region Dependency injection
    private readonly fb = inject(NonNullableFormBuilder);
    //#endregion

    //#region Constants
    public readonly id = 'transfer-building-ownership';
    //#endregion

    //#region Form state
    private readonly _form = this.setupForm();
    public readonly form = toSignalForm(this._form);
    public readonly recipientEmailCtrl = computed(
        () => this.form().controls.recipientEmail
    );
    //#endregion

    //#region Public methods
    public validateAndBuildPayload(): Observable<StartBuildingOwnershipTransferRequest> {
        return defer(() => {
            const form = this.form();

            if (!form.valid) {
                form.markAllAsTouched();
                return EMPTY;
            }

            return of(this.buildPayload(form.getRawValue()));
        });
    }
    //#endregion

    //#region Private methods
    private setupForm(): TransferBuildingOwnershipForm {
        return this.fb.group({
            retainOriginalOwnerAccess: this.fb.control(false),
            recipientEmail: this.fb.control('', {
                validators: [Validators.required, emailValidator()],
            }),
        });
    }

    private buildPayload(
        formValues: TransferBuildingOwnershipFormValues
    ): StartBuildingOwnershipTransferRequest {
        return {
            recipientEmail: formValues.recipientEmail.trim(),
            retainOriginalOwnerAccess: formValues.retainOriginalOwnerAccess,
        };
    }
    //#endregion
}
